import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type AudienceActivationType, AudienceJobStatus, type AudienceType } from '@/api/audience';
import {
  getActivationProviderLogoMap,
  getProviderSquareLogoById,
} from '@/app/integrations/providerUtil';
import ErrorIcon from '@/assets/svg/badges/error-badge.svg?react';
import Plus from '@/assets/svg/bx-plus.svg?react';
import { Button } from '@/components/buttons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { cn } from '@/lib/utils';

type Props = {
  audience: AudienceType;
  addButtonClassName: string;
  onAudienceActivate: () => void;
  onAudienceActivationClick: (activation: AudienceActivationType) => void;
};

const AudienceActivationCell = ({
  audience,
  addButtonClassName,
  onAudienceActivate,
  onAudienceActivationClick,
}: Props) => {
  const activations = audience.activations;
  const activationProviderLogoMap = useMemo(() => getActivationProviderLogoMap(), []);
  const totalActivationCount = Object.keys(activationProviderLogoMap).length;
  const showAddButton = activations && activations?.length < totalActivationCount;

  return (
    <div className="-my-2 flex flex-wrap">
      {activations?.map((activation) => (
        <Tooltip key={activation.provider.id} disableHoverablePopup>
          <TooltipTrigger
            render={
              <div className="relative">
                <Button
                  className="size-8"
                  variant="ghost"
                  size="icon-large"
                  onClick={() => onAudienceActivationClick(activation)}
                >
                  {getProviderSquareLogoById(activation.provider.id)}
                </Button>
                {activation.lastActivationJob?.status === AudienceJobStatus.ERROR && (
                  <ErrorIcon className="pointer-events-none absolute -top-0.5 -right-0.5 size-4 text-red-400" />
                )}
              </div>
            }
          />
          <TooltipContent side="top">{activation.provider.name}</TooltipContent>
        </Tooltip>
      ))}
      {showAddButton && (
        <Tooltip disableHoverablePopup>
          <TooltipTrigger
            render={
              <Button
                className={cn('size-8', addButtonClassName)}
                variant="ghost"
                size="icon"
                aria-label={t`Create Activation`}
                onClick={() => onAudienceActivate()}
              >
                <Plus />
              </Button>
            }
          />
          <TooltipContent side="top">
            <Trans>Create Activation</Trans>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default AudienceActivationCell;
